import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DailyFreeBand } from "@/components/draw/DailyFreeBand";
import { AuthModal } from "@/components/renewal/auth/AuthModal";
import { RevealOverlay } from "@/components/draw/RevealOverlay";
import { useDrawData, usePackReveal } from "@/components/draw/useDrawGacha";
import { Skeleton } from "@/components/ui/Skeleton";
import { api } from "@/lib/api";
import {
  profileDisplay,
  useProfile,
  type ProfileDisplay,
  type ProfileSummary,
} from "@/components/layout/useProfile";
import { cn } from "@shared/lib/utils";

/** Round avatar — user image when present, else initial on an accent gradient. */
export function ProfileAvatar({
  display,
  className,
}: {
  display: ProfileDisplay;
  className?: string;
}) {
  const [imgError, setImgError] = useState(false);

  // A new/different image URL (fresh sign-in, avatar change) deserves a fresh
  // load attempt instead of staying stuck on a previous URL's failure.
  useEffect(() => {
    setImgError(false);
  }, [display.image]);

  if (display.image && !imgError) {
    return (
      <img
        src={display.image}
        alt=""
        onError={() => setImgError(true)}
        className={cn("shrink-0 rounded-full object-cover", className)}
      />
    );
  }
  return (
    <span
      aria-hidden
      className={cn(
        "grid shrink-0 place-items-center rounded-full bg-gradient-to-br from-accent to-accent-dim font-mono font-semibold text-on-accent",
        className,
      )}
    >
      {display.initial}
    </span>
  );
}

const STAT_LABEL = "font-mono text-[9px] font-medium uppercase tracking-[0.16em] text-ink-mute";
const STAT_VALUE = "font-mono text-sm font-medium tabular-nums";

/** The two real account destinations shared by the dropdown and the drawer block. */
const ACCOUNT_ACTIONS = [
  { label: "Profile", to: "/profile" },
  { label: "Settings", to: "/settings" },
] as const;

function StatRow({
  label,
  value,
  valueClassName,
  note,
  loading,
}: {
  label: string;
  value: string;
  valueClassName: string;
  note?: string;
  loading?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className={STAT_LABEL}>{label}</span>
      {loading ? (
        <Skeleton className="h-4 w-12" />
      ) : (
        <span className="flex items-baseline gap-2">
          {note ? (
            <span className="font-mono text-[8px] uppercase tracking-[0.12em] text-white/55">
              {note}
            </span>
          ) : null}
          <span className={cn(STAT_VALUE, valueClassName)}>{value}</span>
        </span>
      )}
    </div>
  );
}

/**
 * Free daily pack claim, embedded in both profile surfaces (dropdown +
 * drawer) — same `useDrawData`/`usePackReveal` DrawPage uses, so the module
 * cache is shared and a claim here or on /draw stays in sync everywhere.
 * Mount this only while its host surface is actually open/visible (see
 * call sites below) — `useDrawData` fetches as soon as it mounts, and this
 * shouldn't happen for every signed-in user on every page.
 */
function DailyFreeProfileSlot() {
  const { loading, cardsState, applyPull, reload } = useDrawData();
  const { reveal, runPull, closeReveal } = usePackReveal(cardsState.items, applyPull, reload);

  return (
    <>
      <DailyFreeBand
        available={cardsState.dailyFreeAvailable}
        nextAt={cardsState.nextDailyFreeAt}
        loading={loading}
        authenticated
        disabled={reveal.pending}
        onPull={() => void runPull(() => api.pullDailyFreeAgentCard())}
        onSignIn={() => {}}
      />
      <RevealOverlay
        open={reveal.open}
        pending={reveal.pending}
        cards={reveal.cards}
        error={reveal.error}
        onClose={closeReveal}
      />
    </>
  );
}

type ProfileCardProps = {
  display: ProfileDisplay;
  /** null until the lazy summary fetch resolves — rows show skeletons. */
  summary: ProfileSummary | null;
  onSignOut: () => void;
};

/**
 * The dropdown card body — flat bg-2 surface, hairline dividers, mono rows.
 * Positioning/mechanics live in ProfileChip; the drawer variant is below.
 */
export function ProfileCard({ display, summary, onSignOut }: ProfileCardProps) {
  const navigate = useNavigate();
  const loading = summary === null;
  return (
    <div
      role="menu"
      aria-label="Profile"
      className="relative w-[280px] overflow-hidden rounded-[12px] border border-line bg-(--ty-bg-2) p-4 shadow-[0_16px_48px_-16px_rgba(0,0,0,0.85)]"
    >
      {/* 상단 액센트 헤어라인 — 열림 상태의 1px 디테일 (좌→우 페이드) */}
      <span
        aria-hidden
        className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-accent/70 via-accent/20 to-transparent"
      />
      <div className="flex items-center gap-3">
        <ProfileAvatar display={display} className="size-10 text-sm" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-ink">{display.name}</p>
          {display.handle ? (
            <p className="truncate font-mono text-[10px] text-ink-mute">{display.handle}</p>
          ) : null}
        </div>
      </div>

      <div aria-hidden className="my-3.5 h-px bg-line" />

      <div className="flex flex-col gap-2.5">
        <StatRow label="IP" value={(summary?.ip ?? 0).toLocaleString()} valueClassName="text-accent" loading={loading} />
        <StatRow label="RP" value={(summary?.rp ?? 0).toLocaleString()} valueClassName="text-yellow" loading={loading} />
        <StatRow label="Rank" value="—" valueClassName="text-ink-mute" note="coming soon" />
      </div>

      <div aria-hidden className="my-3.5 h-px bg-line" />

      <DailyFreeProfileSlot />

      <div aria-hidden className="my-3.5 h-px bg-line" />

      {/* Real navigations — the route change closes the dropdown (ProfileChip effect). */}
      <div className="flex flex-col gap-0.5">
        {ACCOUNT_ACTIONS.map((action) => (
          <button
            key={action.to}
            type="button"
            role="menuitem"
            onClick={() => navigate(action.to)}
            className="flex items-center rounded-lg px-2 py-2 text-left font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-ink-dim transition-colors duration-200 hover:bg-white/4 hover:text-ink"
          >
            {action.label}
          </button>
        ))}
      </div>

      <button
        type="button"
        role="menuitem"
        onClick={onSignOut}
        className="mt-3 inline-flex w-full items-center justify-center rounded-full border border-line px-4 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-ink-dim transition-colors duration-200 hover:border-line-hi hover:text-ink"
      >
        Sign out
      </button>
    </div>
  );
}

type DrawerProfileProps = {
  /** True while the drawer is visible — gates the lazy summary fetch. */
  active: boolean;
  /** Mirror of SideNav's tabbable so hidden-drawer items leave the tab order. */
  tabbable: boolean;
  /** Called after a navigation action so the drawer closes immediately. */
  onNavigate?: () => void;
};

/**
 * Mobile drawer header block: signed in → avatar + name + mono IP · RP row +
 * Profile / Settings / Sign out; signed out → "Log in / Sign up" → AuthModal.
 */
export function DrawerProfile({ active, tabbable, onNavigate }: DrawerProfileProps) {
  const navigate = useNavigate();
  const { user, authenticated, sessionPending, summary, ensureSummary, signOut } = useProfile();
  const tabIndex = tabbable ? 0 : -1;
  const [authOpen, setAuthOpen] = useState(false);

  useEffect(() => {
    if (active && authenticated) ensureSummary();
  }, [active, authenticated, ensureSummary]);

  if (sessionPending) return null;

  if (!authenticated) {
    return (
      <div className="shrink-0 border-b border-line px-5 py-4">
        <button
          type="button"
          tabIndex={tabIndex}
          onClick={() => setAuthOpen(true)}
          className="ty-cta inline-flex w-full items-center justify-center rounded-full px-4 py-2.5 font-mono text-[11px] font-medium uppercase tracking-[0.12em]"
        >
          Log in / Sign up
        </button>
        <AuthModal open={authOpen} initialMode="login" onClose={() => setAuthOpen(false)} />
      </div>
    );
  }

  const display = profileDisplay(user);
  const loading = summary === null;

  return (
    <div className="shrink-0 border-b border-line px-5 py-4">
      <div className="flex items-center gap-3">
        <ProfileAvatar display={display} className="size-9 text-xs" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-ink">{display.name}</p>
          {display.handle ? (
            <p className="truncate font-mono text-[10px] text-ink-mute">{display.handle}</p>
          ) : null}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="flex items-baseline gap-2">
          <span className={STAT_LABEL}>IP</span>
          {loading ? (
            <Skeleton className="h-4 w-10" />
          ) : (
            <span className={cn(STAT_VALUE, "text-accent")}>{summary.ip.toLocaleString()}</span>
          )}
        </span>
        <span aria-hidden className="font-mono text-[10px] text-ink-mute/60">
          ·
        </span>
        <span className="flex items-baseline gap-2">
          <span className={STAT_LABEL}>RP</span>
          {loading ? (
            <Skeleton className="h-4 w-10" />
          ) : (
            <span className={cn(STAT_VALUE, "text-yellow")}>{summary.rp.toLocaleString()}</span>
          )}
        </span>
      </div>

      {/* Gated on `active` (drawer open) — DrawerProfile itself stays mounted
          off-screen while closed, and useDrawData fetches as soon as it
          mounts, so this only mounts while actually visible. */}
      {active ? (
        <div className="mt-3">
          <DailyFreeProfileSlot />
        </div>
      ) : null}

      {/* Compact action row — same three actions as the desktop dropdown. */}
      <div className="mt-3 grid grid-cols-2 gap-2">
        {ACCOUNT_ACTIONS.map((action) => (
          <button
            key={action.to}
            type="button"
            tabIndex={tabIndex}
            onClick={() => {
              navigate(action.to);
              onNavigate?.();
            }}
            className="inline-flex items-center justify-center rounded-full border border-line px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-ink-dim transition-colors duration-200 hover:border-line-hi hover:text-ink"
          >
            {action.label}
          </button>
        ))}
      </div>

      <button
        type="button"
        tabIndex={tabIndex}
        onClick={() => void signOut()}
        className="mt-2 inline-flex w-full items-center justify-center rounded-full border border-line px-4 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-ink-dim transition-colors duration-200 hover:border-line-hi hover:text-ink"
      >
        Sign out
      </button>
    </div>
  );
}
